import { ref, computed, type Ref } from 'vue'
import { sdk } from '@/graphql/client'

export interface Price {
    id: number
    name: string
    title: string
    people: number
    price: number
    currency: string
}

export interface Ticketset {
    id: number
    name: string
    customName: string
    prefix: string
    validate: boolean
    entries: number
    entryPause: number
    unlimited: boolean
    bzucoId: number
    ticketCount: number
    validatedCount: number
    event: { id: number; name: string } | null
    prices: Price[]
}

export interface TicketsetFilters {
    search: string
    event: string | null
}

function normalize(s: string): string {
    return s.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase()
}

export function useTicketsetsData() {
    const ticketsets: Ref<Ticketset[]> = ref([])
    const loading = ref(false)
    const error = ref<string | null>(null)

    const filters: Ref<TicketsetFilters> = ref({
        search: '',
        event: null
    })

    async function refetch() {
        loading.value = true
        error.value = null
        try {
            const r = await sdk.AdminTicketsetsList()
            ticketsets.value = (r.admin?.ticketset?.list ?? []) as Ticketset[]
        } catch (e) {
            error.value = (e as Error).message
            ticketsets.value = []
        } finally {
            loading.value = false
        }
    }

    const filteredTicketsets = computed<Ticketset[]>(() => {
        const f = filters.value
        const q = f.search ? normalize(f.search) : ''

        const filtered = ticketsets.value.filter(ts => {
            if (q && !normalize(ts.name).includes(q)) return false
            if (f.event !== null && String(ts.event?.id ?? '') !== f.event) return false
            return true
        })

        return filtered.slice().sort((a, b) => {
            const ae = a.event?.name ?? null
            const be = b.event?.name ?? null
            if (ae === null && be === null) return a.name.localeCompare(b.name, 'cs')
            if (ae === null) return 1
            if (be === null) return -1
            const ec = ae.localeCompare(be, 'cs')
            if (ec !== 0) return ec
            return a.name.localeCompare(b.name, 'cs')
        })
    })

    return { ticketsets, filters, filteredTicketsets, loading, error, refetch }
}
